import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard, ShoppingCart, Users, CreditCard,
  FileText, Milestone, AlertOctagon, TrendingUp,
  BookOpen, FileSpreadsheet, Settings, LogOut, Bell,
  ChevronDown, Trash2, Building2, RefreshCw,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { ConfirmModal } from './AppModal';
import { resetTestData, deduplicateSeedData, runNightlyBatch } from '../../services/api';

// ── Nav items for top bar ────────────────────────────────────────
const NAV_GROUPS = [
  {
    label: 'Sales',
    icon: ShoppingCart,
    items: [
      { label: 'Sales Orders', path: '/purchase-orders' },
      { label: 'Payment Terms',   path: '/payment-terms' },
      { label: 'Auto Renew',      path: '/auto-renew' },
      { label: 'Milestones',      path: '/milestones' },
    ],
  },
  {
    label: 'Finance',
    icon: FileText,
    items: [
      { label: 'Invoices',            path: '/invoices' },
      { label: 'Unbilled Revenue',    path: '/unbilled-revenue' },
      { label: 'Revenue Recognition', path: '/revenue' },
      { label: 'Account Ledger',      path: '/account-ledger' },
      { label: 'Disputes',            path: '/disputes' },
      { label: 'Bulk Upload',         path: '/bulk-upload' },
    ],
  },
];

// ── Sidebar icon items ───────────────────────────────────────────
const SIDEBAR_ITEMS = [
  { icon: LayoutDashboard, path: '/dashboard',       tooltip: 'Dashboard' },
  { icon: ShoppingCart,    path: '/purchase-orders',  tooltip: 'Sales Orders' },
  { icon: Users,           path: '/customers',        tooltip: 'Customers' },
  { icon: CreditCard,      path: '/payment-terms',    tooltip: 'Payment Terms' },
  { icon: RefreshCw,       path: '/auto-renew',         tooltip: 'Auto Renew' },
  { icon: FileText,        path: '/invoices',         tooltip: 'Invoices' },
  { icon: Milestone,       path: '/milestones',       tooltip: 'Milestones' },
  { icon: AlertOctagon,    path: '/disputes',         tooltip: 'Disputes' },
  { icon: TrendingUp,      path: '/revenue',          tooltip: 'Revenue Recognition' },
  { icon: BookOpen,        path: '/unbilled-revenue', tooltip: 'Unbilled Revenue' },
  { icon: FileSpreadsheet, path: '/term-templates',   tooltip: 'Term Templates' },
  { icon: BookOpen,        path: '/account-ledger',   tooltip: 'Account Ledger' },
];

export default function Layout({ children }) {
  const { user, logout, isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openGroup, setOpenGroup] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [deduping, setDeduping]   = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmDedupe, setConfirmDedupe] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  const handleReset = async () => {
    setResetting(true);
    try {
      const res = await resetTestData();
      const total = Object.values(res.data.deleted || {}).reduce((s, v) => s + v, 0);
      toast.success(`${total} records deleted. Test data cleared.`);
      setConfirmReset(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Reset failed');
    } finally { setResetting(false); setShowUserMenu(false); }
  };

  const handleDeduplicate = async () => {
    setDeduping(true);
    try {
      const res = await deduplicateSeedData();
      const r = res.data.removed || {};
      toast.success(`Deduplicated: ${r.organisations} orgs, ${r.customers} customers, ${r.users} users removed.`);
      setConfirmDedupe(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Deduplication failed');
    } finally { setDeduping(false); setShowUserMenu(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bt-gray-50)' }}>

      {/* ── Top Navigation Bar ──────────────────────────────────── */}
      <header className="bt-topnav">
        {/* Logo — actual BillionTech logo */}
        <NavLink to="/dashboard" className="bt-logo" style={{ minWidth: 160 }}>
          <img
            src="/billiontech-logo.png"
            alt="BillionTech"
            style={{ height: 28, width: 'auto', objectFit: 'contain', maxWidth: 150 }}
            onError={e => {
              e.target.style.display = 'none';
              document.getElementById('bt-logo-fallback').style.display = 'flex';
            }}
          />
          <span id="bt-logo-fallback" style={{ display: 'none', alignItems: 'center', gap: 6,
            fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 800, fontSize: 16,
            color: 'var(--bt-gray-900)' }}>
            <span style={{ color: 'var(--bt-orange)', fontSize: 18 }}>✕</span>BillionTech
          </span>
        </NavLink>

        <div style={{ width: 1, height: 28, background: 'var(--bt-gray-200)', margin: '0 12px', flexShrink: 0 }}/>

        {/* Centre nav groups */}
        <nav className="bt-nav-items">
          <NavLink to="/dashboard"
            className={({ isActive }) => `bt-nav-item${isActive ? ' active' : ''}`}>
            <LayoutDashboard size={14}/> Dashboard
          </NavLink>

          {/* Platform Admin gets a dedicated nav item instead of regular groups */}
          {isPlatformAdmin && (
            <NavLink to="/platform-admin"
              className={({ isActive }) => `bt-nav-item${isActive ? ' active' : ''}`}>
              <Building2 size={14}/> Platform Admin
            </NavLink>
          )}

          {!isPlatformAdmin && NAV_GROUPS.map(group => {
            const isGroupActive = group.items.some(i => location.pathname.startsWith(i.path));
            const isOpen = openGroup === group.label;
            return (
              <div key={group.label} style={{ position: 'relative' }}>
                <button
                  className={`bt-nav-item${isGroupActive ? ' active' : ''}`}
                  onClick={() => setOpenGroup(isOpen ? null : group.label)}
                >
                  <group.icon size={14}/>
                  {group.label}
                  <ChevronDown size={11} style={{ marginLeft: 2, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}/>
                </button>
                {isOpen && (
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0,
                    background: '#fff', border: '1px solid var(--bt-gray-200)',
                    borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                    minWidth: 180, zIndex: 400, padding: '4px 0', overflow: 'hidden',
                  }}>
                    {group.items.map(item => (
                      <NavLink key={item.path} to={item.path}
                        className={({ isActive }) =>
                          `bt-nav-item${isActive ? ' active' : ''}`
                        }
                        style={{ display: 'flex', borderRadius: 0, padding: '8px 14px' }}
                        onClick={() => setOpenGroup(null)}
                      >
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {/* end !isPlatformAdmin */}
        </nav>

        {/* Right side */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
          {/* Company badge */}
          {(user?.organisation_name || isPlatformAdmin) && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '4px 10px', borderRadius: 6,
            background: 'var(--bt-orange-light)',
            border: '1px solid var(--bt-orange-border)',
          }}>
            <div style={{
              width: 18, height: 18, borderRadius: 4,
              background: 'var(--bt-orange)', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              color: 'white', fontSize: 9, fontWeight: 700,
            }}>BT</div>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--bt-orange)' }}>
              {user?.organisation_name || 'Platform Admin'}
            </span>
          </div>
          )}

          {/* Notification bell */}
          <button style={{
            width: 32, height: 32, borderRadius: 6,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--bt-gray-500)', background: 'none', border: 'none', cursor: 'pointer',
          }}>
            <Bell size={16}/>
          </button>

          {/* User avatar + dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowUserMenu(m => !m)}
              style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'var(--bt-orange)', color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                font: '600 13px Inter', border: 'none', cursor: 'pointer',
              }}
            >
              {user?.name?.[0]?.toUpperCase() || 'U'}
            </button>
            {showUserMenu && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                background: '#fff', border: '1px solid var(--bt-gray-200)',
                borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                minWidth: 220, zIndex: 400, overflow: 'hidden',
                padding: '8px 0',
              }}>
                {/* User info */}
                <div style={{ padding: '10px 14px 10px', borderBottom: '1px solid var(--bt-gray-100)' }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--bt-gray-900)' }}>{user?.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--bt-gray-400)', marginTop: 2 }}>{user?.email}</div>
                  <div style={{
                    display: 'inline-block', marginTop: 5,
                    padding: '1px 7px', borderRadius: 10,
                    background: 'var(--bt-orange-light)', color: 'var(--bt-orange)',
                    fontSize: 10, fontWeight: 600,
                  }}>{user?.role}</div>
                </div>
                {/* Settings link */}
                {user?.role === 'ADMIN' && (
                  <NavLink to="/settings"
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', color: 'var(--bt-gray-600)', textDecoration: 'none', fontSize: 13 }}
                    onClick={() => setShowUserMenu(false)}>
                    <Settings size={14}/> Settings
                  </NavLink>
                )}
                {/* Reset test data — ADMIN only */}
                {user?.role === 'ADMIN' && (
                  <>
                    <button
                      onClick={() => { setConfirmDedupe(true); setShowUserMenu(false); }}
                      disabled={deduping}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '9px 14px', color: 'var(--bt-amber)',
                        background: 'none', border: 'none', cursor: 'pointer',
                        width: '100%', fontSize: 13, textAlign: 'left',
                        borderTop: '1px solid var(--bt-gray-100)',
                      }}
                    >
                      <Users size={14}/> {deduping ? 'Deduplicating…' : 'Fix Duplicate Seed Data'}
                    </button>
                    <button
                      onClick={() => { setConfirmReset(true); setShowUserMenu(false); }}
                      disabled={resetting}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '9px 14px', color: 'var(--bt-red)',
                        background: 'none', border: 'none', cursor: 'pointer',
                        width: '100%', fontSize: 13, textAlign: 'left',
                      }}
                    >
                      <Trash2 size={14}/> {resetting ? 'Clearing…' : 'Clear Test Data'}
                    </button>
                  </>
                )}
                {/* Logout */}
                <button
                  onClick={handleLogout}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '9px 14px', color: 'var(--bt-gray-600)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    width: '100%', fontSize: 13, textAlign: 'left',
                    borderTop: '1px solid var(--bt-gray-100)',
                  }}
                >
                  <LogOut size={14}/> Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Left icon sidebar ───────────────────────────────────── */}
      <aside className="bt-sidebar">
        {/* Platform Admin only sees platform admin icon */}
        {isPlatformAdmin ? (
          <NavLink to="/platform-admin" data-tooltip="Platform Admin"
            className={({ isActive }) => `bt-sidebar-icon${isActive ? ' active' : ''}`}>
            <Building2 size={17}/>
          </NavLink>
        ) : (
          SIDEBAR_ITEMS.map(({ icon: Icon, path, tooltip }) => (
            <NavLink key={path} to={path} data-tooltip={tooltip}
              className={({ isActive }) => `bt-sidebar-icon${isActive ? ' active' : ''}`}>
              <Icon size={17}/>
            </NavLink>
          ))
        )}
        <div style={{ flex: 1 }}/>
        {user?.role === 'ADMIN' && (
          <NavLink to="/settings" data-tooltip="Settings"
            className={({ isActive }) => `bt-sidebar-icon${isActive ? ' active' : ''}`}>
            <Settings size={17}/>
          </NavLink>
        )}
      </aside>

      {/* ── Main content ────────────────────────────────────────── */}
      <main className="bt-main">
        {children}
      </main>

      {/* Click-outside to close menus — z-index 95 stays UNDER the topnav (100) */}
      {(openGroup || showUserMenu) && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 95 }}
          onMouseDown={() => { setOpenGroup(null); setShowUserMenu(false); }}/>
      )}

      {confirmReset && (
        <ConfirmModal
          title="Clear Test Data"
          message={'Delete ALL transaction data (POs, invoices, payment terms, schedules)?\n\nOrganisations, users, and customers will be kept.\n\nThis cannot be undone.'}
          confirmLabel="Clear All"
          onConfirm={handleReset}
          onClose={() => setConfirmReset(false)}
          loading={resetting}
          danger
        />
      )}
      {confirmDedupe && (
        <ConfirmModal
          title="Fix Duplicate Seed Data"
          message={'Remove duplicate organisations, customers and users?\n\nOnly the first (oldest) record of each will be kept.'}
          confirmLabel="Deduplicate"
          onConfirm={handleDeduplicate}
          onClose={() => setConfirmDedupe(false)}
          loading={deduping}
        />
      )}
    </div>
  );
}
